import type { Account, Owner, Transaction } from "../types";
import { roundMoney } from "./money-math";
import { isSpendable } from "./finance";

import type { ProfileBalance, SettlementSummary, DebtLink } from "../types/settlement";
export type { ProfileBalance, SettlementSummary, DebtLink } from "../types/settlement";

/**
 * Calculates net contributions, fair shares, and inter-profile debts.
 * Supports:
 * - Equal split (e.g. 50/50 among household or specified participants)
 * - Custom pool (explicit amounts per profile for big-ticket purchases)
 * - Purely joint and personal expenses (which generate 0 debt)
 * - Settlement transfers (reimbursements between partner accounts)
 */
export function calculateSettlement(
  transactions: Transaction[],
  accounts: Pick<Account, "id" | "owner">[],
  profiles: Owner[],
  weights?: ReadonlyMap<string, number>,
): SettlementSummary {
  const profileList = profiles.filter((p) => typeof p === "string" && p.trim().length > 0);
  const accountOwnerMap = new Map<string, Owner>();
  for (const a of accounts) {
    if (a.owner) accountOwnerMap.set(a.id, a.owner);
  }

  const paidMap = new Map<Owner, number>();
  const shareMap = new Map<Owner, number>();
  const settlementDelta = new Map<Owner, number>();

  for (const p of profileList) {
    paidMap.set(p, 0);
    shareMap.set(p, 0);
    settlementDelta.set(p, 0);
  }

  let hasActivity = false;
  let totalSharedSpend = 0;

  for (const t of transactions) {
    const weight = weights?.get(t.id) ?? 1;
    // 1. Settlement transfers
    if (t.type.toLowerCase() === "transfer" && t.isSettlement) {
      const fromOwner = (t.accountId ? accountOwnerMap.get(t.accountId) : undefined) || t.owner;
      const toOwner = (t.toAccountId ? accountOwnerMap.get(t.toAccountId) : undefined) || t.toOwner;
      if (fromOwner && toOwner && fromOwner !== toOwner && paidMap.has(fromOwner) && paidMap.has(toOwner)) {
        hasActivity = true;
        const amount = Math.abs(t.amountBase ?? t.amount) * weight;
        // From-owner paid down debt (+credit towards balance)
        settlementDelta.set(fromOwner, (settlementDelta.get(fromOwner) || 0) + amount);
        // To-owner received reimbursement (-credit towards balance)
        settlementDelta.set(toOwner, (settlementDelta.get(toOwner) || 0) - amount);
      }
      continue;
    }

    // 2. Spendable expenses with splits
    if (!isSpendable(t)) continue;
    const split = t.split;
    if (!split || split.type === "joint" || split.type === "personal") continue;

    const payer = (t.accountId ? accountOwnerMap.get(t.accountId) : undefined) || t.owner;
    if (!payer || !paidMap.has(payer)) continue;

    const total = Math.abs(t.amountBase ?? t.amount);
    if (total <= 0) continue;

    if (split.type === "equal") {
      const participants = (split.participants && split.participants.length > 0
        ? split.participants.filter((p) => paidMap.has(p))
        : profileList);
      if (participants.length === 0) continue;

      hasActivity = true;
      totalSharedSpend += total * weight;
      paidMap.set(payer, (paidMap.get(payer) || 0) + total * weight);

      const count = participants.length;
      const eachShare = roundMoney(total / count);
      let allocated = 0;

      for (let i = 0; i < count; i++) {
        const participant = participants[i];
        // Ensure rounding cents match the exact total on the last participant
        const share = (i === count - 1) ? roundMoney(total - allocated) : eachShare;
        allocated += share;
        shareMap.set(participant, (shareMap.get(participant) || 0) + share * weight);
      }
    } else if (split.type === "custom") {
      const shares = split.shares || {};
      hasActivity = true;
      totalSharedSpend += total * weight;
      paidMap.set(payer, (paidMap.get(payer) || 0) + total * weight);

      for (const [profile, shareAmount] of Object.entries(shares)) {
        if (paidMap.has(profile)) {
          const num = Number(shareAmount);
          if (Number.isFinite(num) && num > 0) {
            shareMap.set(profile, (shareMap.get(profile) || 0) + num * weight);
          }
        }
      }
    }
  }

  // Calculate net balance for each profile
  const balances: ProfileBalance[] = profileList.map((profile) => {
    const paid = roundMoney(paidMap.get(profile) || 0);
    const share = roundMoney(shareMap.get(profile) || 0);
    const delta = roundMoney(settlementDelta.get(profile) || 0);
    const net = roundMoney(paid - share + delta);
    return {
      profile,
      paidShared: paid,
      fairShare: share,
      net,
    };
  });

  // Calculate minimal simplified debt pairs
  const debts = simplifyDebts(balances);

  return {
    hasActivity,
    balances,
    debts,
    totalSharedSpend: roundMoney(totalSharedSpend),
  };
}

/**
 * Greedy debt matching algorithm to find minimum pairwise payments between profiles.
 */
export function simplifyDebts(balances: ProfileBalance[]): DebtLink[] {
  const creditors: { profile: Owner; remaining: number }[] = [];
  const debtors: { profile: Owner; remaining: number }[] = [];

  for (const b of balances) {
    if (b.net > 0.005) {
      creditors.push({ profile: b.profile, remaining: b.net });
    } else if (b.net < -0.005) {
      debtors.push({ profile: b.profile, remaining: Math.abs(b.net) });
    }
  }

  creditors.sort((a, b) => b.remaining - a.remaining);
  debtors.sort((a, b) => b.remaining - a.remaining);

  const debts: DebtLink[] = [];
  let cIndex = 0;
  let dIndex = 0;

  while (cIndex < creditors.length && dIndex < debtors.length) {
    const creditor = creditors[cIndex];
    const debtor = debtors[dIndex];
    const amount = roundMoney(Math.min(creditor.remaining, debtor.remaining));

    if (amount > 0.005) {
      debts.push({
        from: debtor.profile,
        to: creditor.profile,
        amount,
      });
    }

    creditor.remaining = roundMoney(creditor.remaining - amount);
    debtor.remaining = roundMoney(debtor.remaining - amount);

    if (creditor.remaining <= 0.005) cIndex++;
    if (debtor.remaining <= 0.005) dIndex++;
  }

  return debts;
}
